// University of Hawaii at Manoa: public college page (#2652), official facts
// only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UH Manoa's Common Data Set 2025-2026 (Common_Data_Set_2025-2026_v2.pdf,
//    linked from the Mapping, Institutional Research and Outcomes (MIRO)
//    Reports page; read from the PDF and its rendered pages). C1 applicants,
//    admits and enrollees for Fall 2025 with the residency table; C8 test
//    policy for Fall 2027; C21/C22 plans; G1/G5 2026-2027 costs. Version 2
//    differs from the May file only in section H.
//  - UH Manoa Office of Admissions, Freshman Admission Requirements:
//    deadlines (no year shown; the page carries the Fall 2025 admission
//    snapshot and is the page for the current cycle), the test-optional
//    "Do No Harm" policy, the Hawaii resident admission rule, direct
//    admission programs. It publishes no decision dates. No change for fall
//    2028 entry was announced there.
//  - College Scorecard, school id 141574 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1/G5 items match the Financial Aid Services 2026-2027
// Cost of Attendance (living on campus, $33,728 resident and $55,760
// non-resident), which also lists WUE and Pacific Island tuition of
// $17,640. The admissions page's own Fall 2025 snapshot counts 15,302
// admitted (CDS: 15,297) and splits out-of-state applicants into WUE and
// other states; the CDS figures are used.
// The page uses the KB's ASCII spelling of the name; the school writes it
// University of Hawai'i at Manoa with the okina and kahako.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UH Manoa Common Data Set 2025-2026',
        url: 'https://manoa.hawaii.edu/miro/reports/',
        read: '2026-10-01',
    },
    freshman: {
        label: 'UH Manoa Office of Admissions, Freshman Admission Requirements',
        url: 'https://manoa.hawaii.edu/admissions/freshman/index.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?141574-University-of-Hawaii-at-Manoa',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-hawaii-at-manoa',
    kbId: 'university_of_hawaii_at_manoa',
    name: 'University of Hawaii at Manoa',
    shortName: 'UH Manoa',
    aliases: ['UH Manoa', 'UHM', 'Manoa'],
    location: 'Honolulu, HI',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UH Manoa brand colors: Manoa Green,
    // Kelly Green); decoration only.
    branding: {
        primary: '#024731',
        secondary: '#009A44',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_hawaii_at_manoa_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 17389,
        admitted: 15297,
        enrolled: 3319,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 5335, admitted: 4788 },
            { group: 'Out-of-state', applied: 11485, admitted: 10077 },
            { group: 'International', applied: 569, admitted: 432 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UH Manoa\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UH Manoa has no early decision or early action plan; the Common Data Set answers no to both. Its January 5 priority deadline is an earlier filing date, not an early plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Hawaii residents who apply with at least a 3.2 cumulative GPA and have completed the required high school courses are offered admission. Some programs ask for more:',
        rows: [
            { school: 'Shidler College of Business', requirement: 'The Freshman Direct Admit Program lets high-achieving incoming freshmen enter the business college and start its courses early.' },
            { school: 'Nursing', requirement: 'Direct entry for high school graduates set on finishing the BS in nursing in four years; students start in the fall of their first year.' },
            { school: 'Nursing, social work, education and others', requirement: 'Some programs have additional requirements, specific GPAs or prerequisite courses, and may have earlier deadlines. Check with the department.' },
        ],
        note: 'Scholarships and direct entry programs may require test scores.',
        source: 'freshman',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'January 5' },
        rows: [
            { plan: 'Priority deadline', deadline: 'January 5', decision: null, binding: false },
            { plan: 'Final deadline', deadline: 'April 1', decision: null, binding: false },
            { plan: 'Late application deadline', deadline: 'May 1', decision: null, binding: false },
        ],
        note: 'Applying by the priority deadline helps you get resources such as financial aid and student housing. Late applications may be accepted after the final deadline. The $70 application fee is waived for Hawaii residents. UH Manoa does not publish decision dates.',
        source: 'freshman',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UH Manoa is test-optional: you choose whether to send SAT or ACT scores. Under its "Do No Harm" policy, a submitted score that would hurt your chance of admission or a scholarship is left out of the review. You can enter scores on the application or send them to the admissions office.',
        source: 'freshman',
        cdsLine: 'The Common Data Set marks the SAT or ACT as recommended for fall 2027 applicants (and each test alone as not required, but considered if submitted), with scores due by March 1, and notes the move to test-optional.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [11760, 33792] },
            { item: 'Required fees', amounts: [914, 914] },
            { item: 'Housing (on campus)', amounts: [8222, 8222] },
            { item: 'Food (on-campus meal plan)', amounts: [7468, 7468] },
            { item: 'Books and supplies', amounts: [1330, 1330] },
            { item: 'Transportation', amounts: [1336, 1336] },
            { item: 'Other expenses', amounts: [2698, 2698] },
        ],
        totals: [33728, 55760],
        note: 'For a full-time undergraduate living on campus, fall and spring. The Common Data Set notes that tuition and fees vary by program.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 11122,
            byIncome: [
                { income: '$0 to $30,000', amount: 7926 },
                { income: '$30,001 to $48,000', amount: 6845 },
                { income: '$48,001 to $75,000', amount: 9483 },
                { income: '$75,001 to $110,000', amount: 12579 },
                { income: 'Over $110,000', amount: 18901 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UH Manoa\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
